import { useGreeting } from '../../ui/greeting';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, fmtClock, navFor, type OperatorFleet, type ViewId } from '@aithora/shared';
import { useConsoleRole, useSession } from '../../session/SessionContext';
import { roleChipText } from '../../shell/TopBar';
import { sx } from '../../ui/designStyles';
import { shortName, useApi } from './common';
import { upperMedian, worstFirst } from './operatorBlocks';

/**
 * The Partner's own Overview (Console V5; design T7790-7835, ptLayer V5 L37974): read only,
 * across buildings. The facilities worst first, each against the peer median (the same
 * operator fleet route the Operator's Overview reads), and only the pages the Partner can
 * open, each with a line on what is there. Nothing here writes.
 */

/** What each page holds for the Partner (ptLayer NOTE). A page missing here is not offered. */
const NOTE: Partial<Record<ViewId, string>> = {
  residents: 'Rosters and charts, view only',
  worklist: 'Shift work by building',
  family: 'What families see',
  oversight: 'Truth Ledger and decisions',
  atlas: 'Ask across buildings, read only',
  policy: 'Guards and playbooks, read only',
  finance: 'Facility billing and revenue',
  capture: 'Claims and readiness',
  performance: 'Scores against peers',
  companion: 'Conversations, view only',
  settings: 'Your account',
};
const SHOWN = 8;
const tone = (c: number, med: number): [string, string, string] =>
  c >= 80 ? ['Strong', '#1F6B52', 'rgba(47,138,111,.15)'] : c >= med ? ['Watch', '#4A5866', 'rgba(91,107,124,.16)'] : ['Attention', '#B3261E', 'rgba(179,38,30,.13)'];
const ROW = 'display:grid;grid-template-columns:minmax(0,1fr) auto auto auto;align-items:center;gap:12px;min-height:44px;padding:6px 10px;border-radius:6px;text-decoration:none;color:#22272E;border-top:1px solid rgba(34,39,46,.08);';

export function PartnerOverview() {
  const { session } = useSession();
  const hello = useGreeting();
  const { role } = useConsoleRole();
  const fleet = useApi<OperatorFleet>((c, signal) => api.operatorFleet(c, { signal }));
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(t);
  }, []);
  const rows = fleet.data?.facilities ?? [];
  const vals = rows.map((r) => r.score.value).filter((v): v is number => v !== null);
  const med = upperMedian(vals);
  const links = navFor(role).filter((n) => NOTE[n.view]);
  return (
    <div data-screen-label="Overview · Partner" data-testid="partner-overview" style={sx('display:flex;flex-direction:column;flex:0 0 auto;min-width:0;gap:14px;padding-bottom:16px;')}>
      <div style={sx('display:flex;align-items:flex-end;gap:9px 16px;flex-wrap:wrap;flex:0 0 auto;min-width:0;padding-bottom:8px;border-bottom:1px solid rgba(34,39,46,.14);')}>
        <span style={sx('flex:1 1 260px;min-width:0;')}>
          <span style={sx('display:block;font-size:14px;font-weight:600;letter-spacing:-.012em;line-height:1.25;color:#22272E;')}>{`${hello}.`}</span>
          <span style={sx('display:block;font-size:14px;color:#54606C;line-height:1.3;text-wrap:pretty;')}>
            <span>{session ? roleChipText(session, role) : ''}</span> · <span style={sx('font-variant-numeric:tabular-nums;')}>{fmtClock(now)}</span>
          </span>
        </span>
        <span style={sx('flex:0 0 auto;display:inline-flex;align-items:center;height:24px;padding:0 10px;border-radius:12px;font-size:14px;font-weight:600;letter-spacing:.04em;white-space:nowrap;color:#3C454F;background:rgba(34,39,46,.06);border:1px solid rgba(34,39,46,.16);')}>READ ONLY</span>
      </div>
      <div style={sx('font-size:13px;color:#3C454F;line-height:1.5;max-width:72ch;text-wrap:pretty;')}>You can view every building in the portfolio. Changes, approvals and care actions are made by the Aithora team and each building’s own staff.</div>
      <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(320px,1fr));gap:18px;min-width:0;align-items:start;')}>
        <div style={sx('min-width:0;display:flex;flex-direction:column;gap:2px;')}>
          <div style={sx('display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;padding-bottom:6px;')}>
            <span style={sx('font-size:14px;font-weight:600;color:#22272E;')}>Facilities</span>
            <span style={sx('font-size:14px;color:#54606C;')}>worst first · composite vs typical for peers</span>
            <span style={sx('flex:1 1 auto;')} />
            {rows.length > 0 && <Link to="/performance" style={sx('font-size:14px;font-weight:600;color:#0F6B63;')}>{'See all ' + rows.length + ' facilities →'}</Link>}
          </div>
          {fleet.error && <div role="status" style={sx('font-size:14px;color:#B3261E;padding:6px 10px;')}>{fleet.error.message}</div>}
          {fleet.data && rows.length === 0 && <div style={sx('font-size:14px;color:#54606C;padding:6px 10px;')}>No facilities yet.</div>}
          {worstFirst(rows).slice(0, SHOWN).map((f) => {
            const c = f.score.value;
            const t = c === null ? null : tone(c, med);
            const d = c === null ? null : Math.round(c) - med;
            return (
              <Link key={f.orgId} to="/performance" title={c === null ? 'No composite yet' : 'Composite ' + Math.round(c) + ' against a typical peer score of ' + med} style={sx(ROW)}>
                <span style={sx('min-width:0;')}>
                  <span style={sx('display:block;font-size:13px;font-weight:600;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;')}>{shortName(f.name)}</span>
                  <span style={sx('display:block;font-size:14px;color:#54606C;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;')}>{(f.grade ? 'Grade ' + f.grade : 'Not graded') + (f.peerN ? ' · ' + f.peerN + ' peers' : '')}</span>
                </span>
                <span style={sx('font-size:16px;font-weight:700;font-variant-numeric:tabular-nums;')}>{c === null ? '–' : String(Math.round(c))}</span>
                <span style={sx('font-size:14px;font-weight:600;color:' + (d === null ? '#54606C' : d >= 0 ? '#1F6B52' : '#B3261E') + ';white-space:nowrap;')}>{d === null ? '' : (d > 0 ? '+' : '') + d + ' vs typical'}</span>
                <span style={sx('display:inline-flex;align-items:center;height:22px;padding:0 10px;border-radius:11px;font-size:14px;font-weight:600;white-space:nowrap;' + (t ? 'color:' + t[1] + ';background:' + t[2] + ';' : 'color:#54606C;background:rgba(34,39,46,.06);'))}>{t ? t[0] : 'Not graded'}</span>
              </Link>
            );
          })}
        </div>
        <div style={sx('min-width:0;display:flex;flex-direction:column;gap:2px;')}>
          <div style={sx('font-size:14px;font-weight:600;color:#22272E;padding-bottom:6px;')}>Where you can look</div>
          {links.map((l) => (
            <Link key={l.view} to={l.path} style={sx('display:flex;align-items:center;gap:10px;min-height:44px;padding:6px 10px;border-radius:6px;text-decoration:none;color:#22272E;border-top:1px solid rgba(34,39,46,.08);')}>
              <span style={sx('flex:0 0 auto;font-size:13px;font-weight:600;')}>{l.label}</span>
              <span style={sx('flex:1 1 auto;min-width:0;font-size:14px;color:#54606C;line-height:1.3;')}>{NOTE[l.view]}</span>
              <span style={sx('flex:0 0 auto;color:#0F6B63;font-weight:600;')}>→</span>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
